import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { CrossIcon } from "@/components/common/icons";
import useCreatePackageCheckout from "@/components/online-store/checkout/useCreatePackageCheckout";
import { FormPhoneField } from "@/components/serviceFlow/clientDetails/forms/formFields";
import useMoxieMediaQuery from "@/hooks/misc/useMoxieMediaQuery";
import { giftFormSchema } from "@/hooks/onlineStore/useGiftFormUtils";
import { MedspaPackage } from "@/views/online-store/onlineStoreView";

export type GiftRecipientInput = {
  recipientFirstName: string;
  recipientLastName: string;
  recipientEmail: string;
  recipientPhone: string;
};

export default function BuyAsAGiftModal({
  medspaId,
  isOpen,
  onClose,
  packagesData,
}: {
  medspaId: string;
  isOpen: boolean;
  onClose: () => void;
  packagesData: MedspaPackage[];
}) {
  const isMobile = useMoxieMediaQuery("ipad-ver", "down");

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<GiftRecipientInput>({
    resolver: yupResolver(giftFormSchema),
  });

  const { goToCheckout, isLoading } = useCreatePackageCheckout(
    medspaId,
    packagesData
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: { maxWidth: isMobile ? "400px" : "600px", borderRadius: "16px" },
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h3">{`Who's it for?`}</Typography>
          <IconButton onClick={onClose}>
            <CrossIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box>
          <Typography
            sx={{
              mb: 4,
              textAlign: "left",
            }}
          >
            {`We need this information so that we can add purchased items to the recipient's wallet.`}
          </Typography>
          <Stack
            data-dd-privacy="mask"
            sx={{
              alignItems: "center",
            }}
          >
            <form style={{ width: "100%" }}>
              <Stack
                spacing={2}
                sx={{
                  mb: 4,
                }}
              >
                <Stack direction="row" spacing={2}>
                  <Controller
                    name="recipientFirstName"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Recipient's first name"
                        fullWidth
                        type="text"
                        error={!!errors.recipientFirstName}
                        helperText={
                          errors.recipientFirstName?.message as string
                        }
                      />
                    )}
                  />

                  <Controller
                    name="recipientLastName"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Recipient's last name"
                        fullWidth
                        type="text"
                        error={!!errors.recipientLastName}
                        helperText={errors.recipientLastName?.message as string}
                      />
                    )}
                  />
                </Stack>

                <Controller
                  name="recipientEmail"
                  defaultValue=""
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Recipient's email"
                      fullWidth
                      type="text"
                      error={!!errors.recipientEmail}
                      helperText={errors.recipientEmail?.message as string}
                    />
                  )}
                />
                <FormPhoneField
                  control={control}
                  name="recipientPhone"
                  label="Recipient's phone number"
                  sx={{ backgroundColor: "background.paper" }}
                />
              </Stack>
            </form>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              onClick={onClose}
              disabled={isLoading}
              fullWidth
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              fullWidth
              onClick={(e) => {
                e.preventDefault();
                handleSubmit(goToCheckout)();
              }}
              variant="contained"
              disabled={isLoading}
            >
              Checkout
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
